import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { featuredProjects, categories } from '@/mocks/projects';
import { useProjectI18n } from '@/hooks/useProjectI18n';

export default function Projects() {
  const { t } = useTranslation();
  const { t_title, t_tag, t_location } = useProjectI18n();
  const [activeCategory, setActiveCategory] = useState('Tümü');

  const filtered = activeCategory === 'Tümü'
    ? featuredProjects
    : featuredProjects.filter((p) => p.category === activeCategory);

  const getCategoryLabel = (cat: string) => {
    const key = `projects.categories.${cat}`;
    const translated = t(key);
    return translated !== key ? translated : cat;
  };

  return (
    <section id="projects" className="bg-white py-28 px-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-14">
          <div>
            <div className="inline-flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-full px-4 py-1.5 mb-5">
              <span className="w-1 h-1 bg-amber-500 rounded-full" />
              <span className="text-amber-600 text-xs tracking-[0.2em] uppercase font-semibold" style={{ fontFamily: "'Montserrat', sans-serif" }}>{t('homeProjects.badge')}</span>
            </div>
            <h2 className="text-gray-900 text-4xl md:text-5xl font-bold leading-tight" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              {t('homeProjects.heading1')}<br />
              <span className="text-amber-500">{t('homeProjects.heading2')}</span>
            </h2>
          </div>
          <p className="text-gray-500 text-sm max-w-xs leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>{t('homeProjects.desc')}</p>
        </div>

        <div className="flex flex-wrap gap-2.5 mb-10">
          {categories.map((cat) => (
            <button key={cat} onClick={() => setActiveCategory(cat)} className={`px-5 py-2 rounded-full text-xs tracking-wide transition-all duration-300 cursor-pointer whitespace-nowrap font-medium ${activeCategory === cat ? 'bg-amber-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-amber-50 hover:text-amber-600'}`} style={{ fontFamily: "'Montserrat', sans-serif" }}>
              {getCategoryLabel(cat)}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((project) => (
            <Link key={project.id} to={`/projeler/${project.slug}`} className="group relative overflow-hidden rounded-2xl bg-white border border-gray-100 hover:border-amber-200 transition-all duration-400 cursor-pointer" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
              <div className="w-full h-56 overflow-hidden">
                <img src={project.image} alt={t_title(project)} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700" />
              </div>
              <div className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-amber-600 text-xs tracking-wide bg-amber-50 border border-amber-100 rounded-full px-3 py-1 whitespace-nowrap font-medium" style={{ fontFamily: "'Montserrat', sans-serif" }}>{t_tag(project)}</span>
                  <div className="w-7 h-7 flex items-center justify-center bg-gray-100 group-hover:bg-amber-500 rounded-full transition-colors duration-300">
                    <i className="ri-arrow-right-up-line text-gray-400 group-hover:text-white transition-colors duration-300 text-sm" />
                  </div>
                </div>
                <h3 className="text-gray-900 font-bold text-base mb-1" style={{ fontFamily: "'Montserrat', sans-serif" }}>{t_title(project)}</h3>
                <p className="text-gray-500 text-xs flex items-center gap-1.5" style={{ fontFamily: "'Inter', sans-serif" }}>
                  <i className="ri-map-pin-line text-amber-500 text-xs" />{t_location(project)}
                </p>
              </div>
            </Link>
          ))}
        </div>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-12">
          <Link to="/projeler" className="inline-flex items-center gap-2 bg-amber-500 text-white hover:bg-amber-600 px-8 py-3.5 rounded-full text-sm font-semibold tracking-wide transition-all duration-300 cursor-pointer whitespace-nowrap" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            {t('homeProjects.viewAll')}
            <i className="ri-arrow-right-line" />
          </Link>
          <button onClick={() => { const el = document.querySelector('#contact'); if (el) el.scrollIntoView({ behavior: 'smooth' }); }} className="inline-flex items-center gap-2 border border-amber-400 text-amber-600 hover:bg-amber-500 hover:text-white hover:border-amber-500 px-8 py-3.5 rounded-full text-sm font-medium tracking-wide transition-all duration-300 cursor-pointer whitespace-nowrap" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            {t('homeProjects.contactUs')}
            <i className="ri-arrow-right-line" />
          </button>
        </div>
      </div>
    </section>
  );
}